import { useEffect, useRef } from "react";

/**
 * useWakeLock — hold the screen awake while `enabled` is true. Falls
 * silently on browsers / contexts without the Screen Wake Lock API
 * (iOS Safari in a plain tab, older Android webviews, insecure origins).
 *
 * Re-acquires on `visibilitychange` because the browser automatically
 * releases the sentinel when the tab is backgrounded.
 */
export function useWakeLock(enabled) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (!("wakeLock" in navigator)) return;

    let cancelled = false;
    const acquire = async () => {
      if (cancelled) return;
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        sentinelRef.current = sentinel;
        sentinel.addEventListener?.("release", () => {
          // When the OS releases it (visibility change / power event),
          // leave `sentinelRef` null so the next visibility handler
          // re-requests.
          sentinelRef.current = null;
        });
      } catch {
        // Permission denied, insecure origin, etc. — silent, per spec.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !sentinelRef.current) {
        acquire();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      try {
        sentinelRef.current?.release();
      } catch { /* ignore */ }
      sentinelRef.current = null;
    };
  }, [enabled]);
}
